import { routeKeys } from "../components/layout/subcomponents/listmap";
import {
  createPatientRoute,
  createUserRoute,
  modifyRolesRoute,
  createCenterRoute,
  editCenterRoute,
  deleteCenterRoute,
} from "../routes/reactRoutes";
import { enrichManagementPermissions } from "./managementPermissions";

/** Match pathname to a route pattern (supports :param segments). */
export function matchPathPattern(routePattern, pathname) {
  if (!routePattern || !pathname) return false;
  const pathParts = pathname.split("/");
  const patternParts = routePattern.split("/");
  if (pathParts.length !== patternParts.length) {
    return false;
  }
  for (let i = 0; i < patternParts.length; i++) {
    const part = patternParts[i];
    if (part.startsWith(":") && part.length > 1) {
      if (!pathParts[i]) return false;
      continue;
    }
    if (part !== pathParts[i]) {
      return false;
    }
  }
  return true;
}

const MANAGEMENT_CHILD_KEYS = [
  "userPermissions",
  "rolePermissions",
  "centerPermissions",
  "devicesPermissions",
  "medicationsPermissions",
  "medicationFrequenciesPermissions",
  "medicationHoldTimingsPermissions",
  "insurancesPermissions",
  "indicationsPermissions",
  "specialtiesPermissions",
  "complicationsPermissions",
  "subjectAccessRequestsPermissions",
  "messageTemplatesPermissions",
  "contactDropdownFiltersPermissions",
];

function isSuperAdminRole(roles) {
  if (!Array.isArray(roles)) return false;
  return roles.some((r) =>
    typeof r === "string" ? r === "Super Admin" : r?.name === "Super Admin"
  );
}

function isAdminRole(roles) {
  if (!Array.isArray(roles)) return false;
  return roles.some((r) =>
    typeof r === "string" ? r === "Admin" : r?.name === "Admin"
  );
}

/**
 * Which sidebar/menu key controls this pathname (same idea as SideList).
 * Returns null if this path is not gated by feature permissions (profile, prefs, etc.).
 */
export function resolveProtectedMenuKey(pathname) {
  if (!pathname) return null;

  for (const key of MANAGEMENT_CHILD_KEYS) {
    const routes = routeKeys[key] || [];
    if (routes.some((r) => matchPathPattern(r, pathname))) {
      return key;
    }
  }

  for (const [key, routes] of Object.entries(routeKeys)) {
    if (key === "management" || key === "communication") continue;
    if (!Array.isArray(routes)) continue;
    if (routes.some((r) => matchPathPattern(r, pathname))) {
      return key;
    }
  }

  return null;
}

/**
 * Granular URL checks that differ from simple "read" on the parent menu key.
 * Must stay aligned with PrivateRoute / sidebar expectations.
 */
function checkGranularPath(pathname, permissions, roles) {
  const sa = isSuperAdminRole(roles);
  const p = sa ? permissions || {} : enrichManagementPermissions(permissions || {});

  if (pathname === createPatientRoute) {
    return sa || !!p.patients?.create;
  }
  if (pathname === createUserRoute) {
    return sa || !!p.users?.create;
  }
  if (pathname === modifyRolesRoute) {
    return sa || !!p.roles?.read;
  }
  if (pathname === createCenterRoute) {
    return sa || !!p.locations?.create;
  }
  if (pathname === editCenterRoute) {
    return sa || !!p.locations?.update;
  }
  if (pathname === deleteCenterRoute) {
    return sa || !!p.locations?.delete;
  }

  return null;
}

/**
 * Whether the user may open this pathname (same rules as sidebar visibility + granular create/edit).
 */
export function canAccessPathname(pathname, permissions, roles) {
  const sa = isSuperAdminRole(roles);
  const isAdmin = isAdminRole(roles);
  const p = sa ? permissions || {} : enrichManagementPermissions(permissions || {});

  const granular = checkGranularPath(pathname, permissions, roles);
  if (granular !== null) {
    return granular;
  }

  const key = resolveProtectedMenuKey(pathname);
  if (key === null) {
    return true;
  }

  const hasCommunicationAccess =
    sa || !!p.communication_modes?.read || !!p.communicationModes?.read;

  const childPermissionCheck = buildManagementChildPermissionCheck(p, sa, isAdmin);

  const permissionCheck = {
    dashboard: !!p.analytics?.read,
    patients: !!p.patients?.read,
    tasks: !!p.tasks?.read,
    reports: !!p.reports?.read,
    "case-days": !!p.cases?.read,
    registry: !!p.registry?.read,
    contacts: !!p.contacts?.read,
    centerLocations: sa || !!p.center_locations?.read,
    inbox: sa || !!p.inbox?.read,
    sarRequests: sa || isAdmin || !!p.subject_access_requests?.read,
    communicationMessages: hasCommunicationAccess,
    communicationMessageTemplates: hasCommunicationAccess,
    communicationEmailTemplates: hasCommunicationAccess,
    communicationCalls: hasCommunicationAccess,
    communicationModes: hasCommunicationAccess,
    messages: hasCommunicationAccess,
    management: false,
    pushNotification:
      !!p.pushNotifications?.read || !!p.pushNotificationPermissions?.read,
    userGroups: true,
    clients: true,
    ...childPermissionCheck,
  };

  if (MANAGEMENT_CHILD_KEYS.includes(key)) {
    return !!permissionCheck[key];
  }

  return !!permissionCheck[key];
}

/** Management submenu visibility — settings grants Users, Roles, Devices, and Contact Dropdown Filters. */
export function buildManagementChildPermissionCheck(
  permissions,
  isSuperAdmin,
  isAdmin = false
) {
  const p = permissions || {};
  const sa = !!isSuperAdmin;
  const admin = !!isAdmin;
  const settingsRead = !!p.settings?.read;

  return {
    userPermissions: sa || settingsRead || !!p.users?.read,
    rolePermissions: sa || settingsRead || !!p.roles?.read,
    centerPermissions: sa || !!p.locations?.read,
    devicesPermissions: sa || settingsRead || !!p.devices?.read,
    medicationsPermissions: sa || settingsRead || !!p.medications?.read,
    medicationFrequenciesPermissions: sa || settingsRead || !!p.medication_frequencies?.read,
    medicationHoldTimingsPermissions: sa || settingsRead || !!p.medication_hold_timings?.read,
    insurancesPermissions: sa || settingsRead || !!p.insurances?.read,
    indicationsPermissions: sa || settingsRead || !!p.indications?.read,
    specialtiesPermissions: sa || settingsRead,
    complicationsPermissions: sa || settingsRead || !!p.complications?.read,
    // Full SAR management queue: Super Admin or center Admin only
    subjectAccessRequestsPermissions: sa || admin,
    messageTemplatesPermissions:
      sa ||
      !!p.message_templates?.read ||
      !!p.messageTemplates?.read ||
      !!p.messages?.read,
    contactDropdownFiltersPermissions: sa || settingsRead,
  };
}

export function hasAnyManagementChildAccess(permissions, isSuperAdmin, isAdmin = false) {
  const checks = buildManagementChildPermissionCheck(
    permissions,
    isSuperAdmin,
    isAdmin
  );
  return MANAGEMENT_CHILD_KEYS.some((key) => checks[key]);
}
